// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// Runtime SPA configuration, fetched once from the backend `GET /api/spa-config`
// (same origin).
//
// This is the SPA's ONLY config channel and it is deliberately a runtime fetch,
// never a build-time bake: there are no `VITE_*` / `import.meta.env` flags in
// this bundle. That is what lets the built SPA stay byte-identical across
// environments so production is served the byte-identical *promoted* bundle
// rather than a per-env rebuild — per-env differences are runtime config.
//
// Three consumers today:
//   * `web3auth-config.ts` — the public Web3Auth clientId / key-infra network.
//     It layers its own localhost-only devnet fallback and hard-error semantics
//     on top of what we return here.
//   * COB-453 (SV-1) — `shared_vault_policy_enabled`, the operator toggle that
//     decides whether the shared-vault policy controls (verifier address
//     allowlist, rolling spending limit, verifier-assisted withdrawals) render
//     live or as "coming soon". Server-side, the same value gates
//     `POST /shared-vault` (`policy`) and `POST …/withdrawal` (`uses_verifier`)
//     with a 403, so this only ever mirrors a refusal that already exists —
//     the UI treatment is presentation, never the control.
//   * COB-456 — `shared_vault_max_invitees`, the per-vault invitee cap the
//     create wizard stops at. Same relationship to the server: `POST
//     /shared-vault` re-reads the cap and 400s on its own, so this only
//     decides when the UI stops offering another slot.
//
// Nothing here is a secret: the Web3Auth clientId is a public client-side
// identifier and "is this feature live" is not confidential. The endpoint is
// public (pre-auth) on purpose so the SPA can bootstrap before sign-in.

import { useEffect, useState } from 'preact/hooks';

/** The `GET /api/spa-config` payload, snake_case as served. */
export interface SpaConfig {
  web3auth_client_id: string;
  web3auth_network: string;
  /** COB-453 (SV-1): whether shared-vault policy controls are open. */
  shared_vault_policy_enabled: boolean;
  /** COB-456: the effective per-vault invitee cap (invitees only — the
   *  initiator holds their own slot and is not counted). */
  shared_vault_max_invitees: number;
  /** COB-491 (R5): the verifier's Ed25519 identity pubkey, 64 lowercase hex.
   *  **Not a trusted assertion** — it is served by the party the key agent is
   *  guarding against, and is meaningless without the next two fields. */
  verifier_identity_pubkey_hex: string;
  /** COB-491 (R5): the Cobault root's offline signature over the
   *  `(cobault_env, pubkey)` binding, 128 lowercase hex. The key agent checks
   *  it against the root pubkey compiled into its bundle. */
  verifier_identity_signature_hex: string;
  /** COB-491 (R5): this deployment's environment tag (`staging` | `prod`), the
   *  string bound into the signature above. The key agent derives the env it
   *  expects from the session's bitcoin network and requires this to match —
   *  that is what closes cross-env substitution of a validly-signed tuple. */
  cobault_env: string;
}

/** COB-491 (R5): the three fields the key agent needs to recover the verifier
 *  identity pubkey through the root-signed chain. Same shape the agent's
 *  `VerifierIdentityTuple` declares; kept structural so the SPA does not have
 *  to value-import the key-agent bundle to name it. */
export interface VerifierIdentity {
  verifier_identity_pubkey_hex: string;
  verifier_identity_signature_hex: string;
  cobault_env: string;
}

/**
 * COB-456: the invitee cap the SPA assumes when `/api/spa-config` is
 * unavailable or serves something unusable.
 *
 * 18, NOT the server's product default of 10 — and that gap is deliberate.
 * 18 is the consensus hard bound (20 `OP_CHECKMULTISIG` keys − the initiator −
 * an optional verifier key), i.e. the highest value the server could ever
 * legitimately be configured to. Falling back to it makes the client guard
 * strictly *permissive*: a transient config failure can never grey out an
 * invitee slot the backend would have accepted. The real control is the
 * server's own 400, which re-reads the live cap on every create — so being
 * loose here costs at most one legible error message, while being tight would
 * block a user with no recourse. Same philosophy as the SV-1 fallback above.
 */
export const SHARED_VAULT_MAX_INVITEES_FALLBACK = 18;

/**
 * What we assume when `/api/spa-config` is unreachable or unparseable.
 *
 * `shared_vault_policy_enabled: true` mirrors the server's own default (an
 * absent `runtime_config` override means allow), so a transient failure can
 * never *hide* a capability the backend would happily serve. The reverse
 * mistake — briefly showing a control the server refuses — costs at most one
 * 403 with a legible message, whereas wrongly greying out a live control blocks
 * a user with no recourse.
 *
 * `web3auth_client_id: ''` is the sentinel `web3auth-config.ts` already treats
 * as "not configured".
 */
export const SPA_CONFIG_FALLBACK: SpaConfig = {
  web3auth_client_id: '',
  web3auth_network: 'sapphire_mainnet',
  shared_vault_policy_enabled: true,
  shared_vault_max_invitees: SHARED_VAULT_MAX_INVITEES_FALLBACK,
  // COB-491 (R5): empty strings, and deliberately NOT a permissive fallback.
  // Every other field here falls back loose because the server holds the real
  // control; this one is the opposite — the key agent IS the control, and an
  // empty tuple is what makes it refuse. A config hiccup must cost a legible
  // "cannot verify" refusal, never a signature over an unattested withdrawal.
  verifier_identity_pubkey_hex: '',
  verifier_identity_signature_hex: '',
  cobault_env: '',
};

/**
 * Coerce an arbitrary JSON body into a `SpaConfig`, field by field, falling back
 * per-field rather than all-or-nothing: an older backend that does not yet serve
 * `shared_vault_policy_enabled` still yields a usable Web3Auth config.
 *
 * `shared_vault_policy_enabled` accepts ONLY a real boolean — a missing or
 * non-boolean field is the fallback (`true`, allow), never a truthiness coercion
 * that could turn `"false"` into `true` or `0` into off.
 *
 * `shared_vault_max_invitees` accepts ONLY an integer `number`
 * (`Number.isInteger`, which also rejects `NaN` / `Infinity`). A stringly
 * `"10"`, a fractional `10.5`, or a missing field is the fallback — never a
 * `Number()` coercion that would turn `""` or `null` into `0` and silently
 * forbid every invitee.
 */
export function normalizeSpaConfig(body: unknown): SpaConfig {
  const b = (body ?? {}) as Record<string, unknown>;
  return {
    web3auth_client_id:
      typeof b.web3auth_client_id === 'string'
        ? b.web3auth_client_id
        : SPA_CONFIG_FALLBACK.web3auth_client_id,
    web3auth_network:
      typeof b.web3auth_network === 'string'
        ? b.web3auth_network
        : SPA_CONFIG_FALLBACK.web3auth_network,
    shared_vault_policy_enabled:
      typeof b.shared_vault_policy_enabled === 'boolean'
        ? b.shared_vault_policy_enabled
        : SPA_CONFIG_FALLBACK.shared_vault_policy_enabled,
    shared_vault_max_invitees: Number.isInteger(b.shared_vault_max_invitees)
      ? (b.shared_vault_max_invitees as number)
      : SPA_CONFIG_FALLBACK.shared_vault_max_invitees,
    verifier_identity_pubkey_hex:
      typeof b.verifier_identity_pubkey_hex === 'string'
        ? b.verifier_identity_pubkey_hex
        : SPA_CONFIG_FALLBACK.verifier_identity_pubkey_hex,
    verifier_identity_signature_hex:
      typeof b.verifier_identity_signature_hex === 'string'
        ? b.verifier_identity_signature_hex
        : SPA_CONFIG_FALLBACK.verifier_identity_signature_hex,
    cobault_env:
      typeof b.cobault_env === 'string' ? b.cobault_env : SPA_CONFIG_FALLBACK.cobault_env,
  };
}

// Memoised across the page load. Only a SUCCESSFUL fetch is cached; a failure
// leaves the cache empty so a later call retries (web3auth-config.ts depends on
// that retry — its hard error must be recoverable once the backend is
// reachable). One in-flight promise is shared so concurrent callers make one
// request.
let cached: SpaConfig | null = null;
let inflight: Promise<SpaConfig> | null = null;

async function load(): Promise<SpaConfig | null> {
  try {
    const res = await fetch('/api/spa-config', {
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    return normalizeSpaConfig(await res.json());
  } catch {
    // Network error / endpoint not served (local dev with no backend).
    return null;
  }
}

/** Runtime SPA config, fetched once and memoised on success. */
export function fetchSpaConfig(): Promise<SpaConfig> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = load().then((cfg) => {
    inflight = null;
    if (cfg) cached = cfg;
    return cfg ?? SPA_CONFIG_FALLBACK;
  });
  return inflight;
}

/**
 * The memoised config if it has already arrived, else `null`. Lets a component
 * seed its initial state synchronously so a warm cache renders the final
 * treatment on the FIRST paint — no flash of a control we are presenting as
 * "coming soon".
 */
export function peekSpaConfig(): SpaConfig | null {
  return cached;
}

/** Test-only: drop the memoised config so each case starts cold. */
export function resetSpaConfigCache(): void {
  cached = null;
  inflight = null;
}

/**
 * COB-453 (SV-1): the live shared-vault policy-controls toggle, for the create
 * wizard's policy builder and the withdrawal verifier opt-in.
 *
 * Seeded from the memoised cache so a warm cache is correct on the first paint,
 * then refreshed once on mount. Never a gate in its own right — the server's 403
 * is the control; this only decides whether the UI offers the control or renders
 * it as "coming soon".
 */
export function useSharedVaultPolicyEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(
    () =>
      peekSpaConfig()?.shared_vault_policy_enabled ??
      SPA_CONFIG_FALLBACK.shared_vault_policy_enabled,
  );
  useEffect(() => {
    let cancelled = false;
    void fetchSpaConfig().then((cfg) => {
      if (!cancelled) setEnabled(cfg.shared_vault_policy_enabled);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return enabled;
}

/**
 * COB-456: the live per-vault invitee cap, for the create wizard's chip field.
 *
 * Same shape as {@link useSharedVaultPolicyEnabled}: seeded from the memoised
 * cache so a warm cache is right on the first paint, then refreshed once on
 * mount. Never a gate in its own right — `POST /shared-vault` re-reads the same
 * runtime-config key and returns a 400 of its own; this only decides when the
 * UI stops offering another invitee slot and says why.
 */
export function useSharedVaultMaxInvitees(): number {
  const [cap, setCap] = useState<number>(
    () =>
      peekSpaConfig()?.shared_vault_max_invitees ??
      SPA_CONFIG_FALLBACK.shared_vault_max_invitees,
  );
  useEffect(() => {
    let cancelled = false;
    void fetchSpaConfig().then((cfg) => {
      if (!cancelled) setCap(cfg.shared_vault_max_invitees);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return cap;
}

/** Pull the R5 identity tuple out of a config payload. */
export function verifierIdentityOf(cfg: SpaConfig): VerifierIdentity {
  return {
    verifier_identity_pubkey_hex: cfg.verifier_identity_pubkey_hex,
    verifier_identity_signature_hex: cfg.verifier_identity_signature_hex,
    cobault_env: cfg.cobault_env,
  };
}

/**
 * COB-491 (R5): the root-signed verifier identity tuple, for the withdrawal
 * sign path and the confirm modal's attested label.
 *
 * Same shape as the hooks above — seeded from the memoised cache so a warm
 * cache is right on the first paint, refreshed once on mount. Unlike them it is
 * NOT presentation: what the key agent does with it is refuse or proceed. The
 * empty fallback is therefore the fail-closed value, not a permissive one.
 */
export function useVerifierIdentity(): VerifierIdentity {
  const [identity, setIdentity] = useState<VerifierIdentity>(() => {
    const cached = peekSpaConfig();
    return verifierIdentityOf(cached ?? SPA_CONFIG_FALLBACK);
  });
  useEffect(() => {
    let cancelled = false;
    void fetchSpaConfig().then((cfg) => {
      if (!cancelled) setIdentity(verifierIdentityOf(cfg));
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return identity;
}
